import React, { useCallback, useState } from 'react';
import { useAppContext } from '@prisjakt/ui-app-context';
import { useReviewContext } from 'src/areas/reviews/review-context';
import T from '@prisjakt/ui-translate';

import { Translation, Reply } from '../../types';

import * as S from './styles';

type Props = {
  id: string;
  translation?: Translation | null;
  text?: string | null;
  reply?: Reply | null;
};

const ReviewText = ({ id, translation, text, reply }: Props) => {
  const { t } = useAppContext();
  const { onReviewExpansion } = useReviewContext();

  const [showMore, setShowMore] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [showOriginal, setShowOriginal] = useState(false);

  const textNode = useCallback((node: HTMLParagraphElement | null) => {
    if (node && node.parentElement) {
      const textHeight = node.getBoundingClientRect().height;
      const wrapperHeight = node.parentElement.getBoundingClientRect().height;
      setShowMore(textHeight > wrapperHeight);
    }
  }, []);

  const collapseOrExpand = isExpanded ? 'symbol.collapse' : 'symbol.expand';
  const reviewTextIdentifier = `review-text-${id}`;
  const expansionText = isExpanded
    ? t('common', 'button.show-less')
    : t('common', 'button.show-more');

  const reviewText = showOriginal && translation ? translation.text : text;

  const toggleExpansion = () => {
    setIsExpanded(!isExpanded);
    if (onReviewExpansion) onReviewExpansion();
  };

  const toggleTranslation = () => {
    setShowOriginal(!showOriginal);
  };

  const renderTranslation = () => {
    if (!translation || !translation.text) {
      return null;
    }

    return (
      <S.TranslationOptions>
        <S.TranslationOptionsService>
          <T domain="reviews">
            {translation.service && translation.service.name
              ? `translation.translated-by-${translation.service.name.toLowerCase()}.md`
              : 'translation.translated'}
          </T>
        </S.TranslationOptionsService>
        <S.TranslationOptionsToggle>
          <button type="button" onClick={toggleTranslation}>
            {showOriginal
              ? t('reviews', 'translation.show-translation')
              : t('reviews', 'translation.show-original')}
          </button>
        </S.TranslationOptionsToggle>
      </S.TranslationOptions>
    );
  };

  const renderReply = () => {
    if (!reply || !reply.text) {
      return null;
    }

    return (
      <S.Reply>
        <S.ReplyName>
          {t('reviews', 'store.reply-by', {
            name: reply.name,
          })}
          {reply.date && <S.ReplyDate>{reply.date}</S.ReplyDate>}
        </S.ReplyName>
        <S.ReplyText>{reply.text.trim()}</S.ReplyText>
      </S.Reply>
    );
  };

  return (
    <>
      <S.TextLabel id={reviewTextIdentifier} fullText={isExpanded}>
        <S.Text ref={textNode}>{reviewText && reviewText.trim()}</S.Text>
      </S.TextLabel>

      {showMore && (
        <S.ReadMore
          onClick={toggleExpansion}
          aria-label={t('symbols', collapseOrExpand)}
          aria-expanded={isExpanded}
          aria-controls={reviewTextIdentifier}
        >
          {expansionText}
        </S.ReadMore>
      )}

      {translation && renderTranslation()}
      {reply && renderReply()}
    </>
  );
};

export default ReviewText;
